import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

function generateSessionId(): string {
  const stored = sessionStorage.getItem("visitor_session_id");
  if (stored) return stored;

  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  sessionStorage.setItem("visitor_session_id", id);
  return id;
}

function parseUserAgent(ua: string) {
  let browser = "Unknown";
  let os = "Unknown";
  let deviceType = "desktop";

  if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
  else if (ua.includes("Edg")) browser = "Edge";
  else if (ua.includes("Opera") || ua.includes("OPR")) browser = "Opera";

  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  if (ua.includes("Mobile") || ua.includes("Android") || ua.includes("iPhone")) {
    deviceType = "mobile";
  } else if (ua.includes("Tablet") || ua.includes("iPad")) {
    deviceType = "tablet";
  }

  return { browser, os, deviceType };
}

export function useVisitorTracking() {
  const tracked = useRef(false);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;

    const trackVisit = async () => {
      try {
        const sessionId = generateSessionId();
        const { browser, os, deviceType } = parseUserAgent(navigator.userAgent);
        const { data: { session } } = await supabase.auth.getSession();

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-visitor-ip`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            session_id: sessionId,
            page_path: window.location.pathname,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent,
            device_type: deviceType,
            browser,
            os,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
      } catch {
        // Silently fail - visitor tracking is non-critical
      }
    };

    // Defer tracking out of the critical rendering path
    const schedule = typeof requestIdleCallback === 'function'
      ? requestIdleCallback
      : (cb: () => void) => setTimeout(cb, 2000);

    schedule(() => { trackVisit(); });
  }, []);
}
